import React, { useCallback, useContext, useMemo, useRef, memo } from 'react'
import MonthCalendar from './month-calendar'
import {
    AvailabilityBoxWrapper,
    CalendarNavigationBar,
    DescriptionBar,
    LegendWrapper,
    Legend,
    LegendBar,
} from './styles'
import { useTranslation } from '../../../hooks/locale'
import { ScreenContext, Typography } from 'react-components'
import AvailabilityCalendarBack from './availabily-calendar-back'
import AvailabilityCalendarNext from './availability-calendar-next'
import { ExposeSectionTitleWrapper, StyledGhostButton, CTAButton } from '../styles'
import { SECTION_IDS } from '../constants'
import generateSkeletonData from './generate-skeleton-data'
import { EmblaCarousel } from '../../common/embla-carousel'
import type { EmblaCarouselType } from '../../common/embla-carousel/types'

interface SkeletonAvailabilityCalendarProps {
    isLoading?: boolean
    error?: Error | null
    pastDelay?: boolean
}

const SkeletonAvailabilityCalendar: React.FC<SkeletonAvailabilityCalendarProps> = () => {
    const { t } = useTranslation()
    const { isMobile, isTablet } = useContext(ScreenContext)
    const emblaApiRef = useRef<EmblaCarouselType | null>(null)

    const monthsData = useMemo(() => {
        return generateSkeletonData()
    }, [])

    const handleEmblaInit = useCallback((emblaApi: EmblaCarouselType) => {
        emblaApiRef.current = emblaApi
    }, [])

    const handlePrev = useCallback(() => {
        emblaApiRef.current?.scrollPrev()
    }, [])

    const handleNext = useCallback(() => {
        emblaApiRef.current?.scrollNext()
    }, [])

    return (
        <section id={SECTION_IDS.AVAILABILITY_SECTION_ID}>
            <AvailabilityBoxWrapper>
                <ExposeSectionTitleWrapper>
                    <Typography variant="h6" fontWeight="semiBold" element="h2">
                        {t('availabilityTitle')}
                    </Typography>
                </ExposeSectionTitleWrapper>
                <CalendarNavigationBar>
                    <AvailabilityCalendarBack onClick={handlePrev} />
                    <CTAButton>
                        <StyledGhostButton isPrimary={false} isContentWidth>
                            <Typography variant="subtitle2" fontWeight="regular">
                                {t('calendarViewTypeMonth')}
                            </Typography>
                        </StyledGhostButton>
                    </CTAButton>
                    <AvailabilityCalendarNext numberOfSlides={monthsData.length} onClick={handleNext} />
                </CalendarNavigationBar>
                <EmblaCarousel
                    slides={monthsData.map((month, index) => (
                        <MonthCalendar key={`${month.label}_${index}`} data={month} />
                    ))}
                    visibleSlides={isMobile || isTablet ? 1 : 2}
                    slidesToScroll={1}
                    dragEnabled={false}
                    onEmblaInit={handleEmblaInit}
                />
                <DescriptionBar>
                    <LegendBar>
                        <Typography variant="subtitle1" fontWeight="highlight">
                            {t('legendTranslation')}:
                        </Typography>
                        <LegendWrapper>
                            <Legend available />
                            <Typography variant="subtitle2">{t('freeLabel')}</Typography>
                        </LegendWrapper>
                        <LegendWrapper>
                            <Legend available={false} />
                            <Typography variant="subtitle2">{t('occupiedLabel')}</Typography>
                        </LegendWrapper>
                    </LegendBar>
                </DescriptionBar>
            </AvailabilityBoxWrapper>
        </section>
    )
}

export default memo(SkeletonAvailabilityCalendar)
